// Pricing migration 32 (ADO-3985), T14 and T15: the cart's multi-buy deals for the side cart and
// the order review. Asks for the deals of the cart's products (one read beside the cart, as the
// product pages do, D-2) and summarises them with `cartDealsOf`. With the flag off, no entry or no
// deal data, it says nothing (AC6).

import { useIsWowEntry } from '../basket/wowEntry';
import { useCurrentPriceKey } from '../pricing/usePriceStore';
import { cartDealsOf, type CartDealLine, type CartDealsView } from './cartDeals';
import { useDealSessionStore } from './useDealSessionStore';
import { useMultibuyDeals, useMultibuyDealsStore } from './useMultibuyDealsStore';
import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { realPricesEnabled } from '../pricing/realPrices';

/** `surface` picks which closed flags apply: the side cart's, or the order review's own (R8 14a:
 *  closing the side cart's flag hides nothing at review). */
export function useCartDeals(
  lines: readonly CartDealLine[],
  surface: 'sideCart' | 'review' = 'sideCart',
): CartDealsView {
  // With VITE_REAL_PRICES_ENABLED off, nothing multi-buy shows, the WOW free count included (row
  // 18's DEC-18 for savings).
  const shownLines = realPricesEnabled() ? lines : [];
  useMultibuyDeals(shownLines.map((line) => line.skuCode));
  const wow = useIsWowEntry();
  const lost = useDealSessionStore((s) => s.lost);
  const dismissed = useDealSessionStore((s) =>
    surface === 'review' ? s.dismissedReviewFlags : s.dismissed,
  );
  const currentKey = useCurrentPriceKey();
  const key = useMultibuyDealsStore((s) => s.key);
  const entries = useMultibuyDealsStore((s) => s.entries);
  const dealsBySku: Record<string, readonly MultibuyDeal[]> = {};
  if (currentKey !== null && key === currentKey) {
    for (const [sku, entry] of Object.entries(entries)) {
      if (entry.state === 'ready') dealsBySku[sku] = entry.deals;
    }
  }
  return cartDealsOf(shownLines, dealsBySku, { wow, lost, dismissed });
}
